import { message } from 'ant-design-vue'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  canRole,
  type PermissionAction,
  type PermissionResource,
  ROLE_ADMIN,
} from '@/lib/permissions'
import router from '@/router'
import { neon } from '@/services/neon'
import { updateLastLogin as updateLastLoginInDb } from '@/services/users'
import { useThemeStore } from '@/stores/theme'

const TOKEN_KEY = 'token'
const SESSION_TOKEN_KEY = 'session_token'
const USER_KEY = 'user'
const REMEMBER_KEY = 'remember'

export const useAuthStore = defineStore('auth', () => {
  const storedToken = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(SESSION_TOKEN_KEY)
  const token = ref<string | null>(storedToken)

  const persistedUserRaw = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY)
  const persistedUser = persistedUserRaw ? JSON.parse(persistedUserRaw) : null
  const user = ref<Record<string, any> | null>(persistedUser)

  const remember = ref(localStorage.getItem(REMEMBER_KEY) === 'true')
  const companyMembershipRoles = ref<Record<string, 'owner' | 'manager'>>({})

  // Helper to get the correct storage for user based on remember
  const getUserStorage = () => (remember.value ? localStorage : sessionStorage)
  const persistUser = (newUser: Record<string, any> | null) => {
    if (newUser) {
      const storage = getUserStorage()
      storage.setItem(USER_KEY, JSON.stringify(newUser))
      // Clear the other storage to avoid stale data
      const otherStorage = storage === localStorage ? sessionStorage : localStorage
      otherStorage.removeItem(USER_KEY)
    } else {
      localStorage.removeItem(USER_KEY)
      sessionStorage.removeItem(USER_KEY)
    }
  }

  const isAuthenticated = computed(() => !!token.value)
  const role = computed(() => (user.value?.role as string | undefined) || undefined)
  const isAdmin = computed(() => role.value === ROLE_ADMIN)
  const isApproved = computed(() => user.value?.is_approved !== false)
  const isEmailVerified = computed(() => user.value?.emailVerified !== false)
  const lastRegisteredEmail = ref<string | null>(null)
  const initializing = ref(!!storedToken)

  async function loadUserProfile(userId: string) {
    try {
      const { data, error } = await neon
        .from('users')
        .select(
          'role,is_banned,is_active,is_approved,company_id,subscription_status,subscription_end_date',
        )
        .eq('id', userId)
        .single()

      if (!error && data) {
        user.value = {
          ...(user.value || {}),
          ...(data as Record<string, unknown>),
        }
      }
    } catch (error) {
      console.warn('Unable to fetch user profile context', error)
    }

    if (user.value) {
      persistUser(user.value)
    }
  }

  async function loadCompanyMembershipContext() {
    if (!user.value?.id) {
      companyMembershipRoles.value = {}
      return
    }

    if (isAdmin.value) {
      companyMembershipRoles.value = {}
      return
    }

    try {
      const { data, error } = await neon
        .from('company_members')
        .select('company_id,role')
        .eq('user_id', user.value.id)
        .eq('is_active', true)

      if (error) {
        throw new Error(error.message)
      }

      const rolesByCompany: Record<string, 'owner' | 'manager'> = {}
      ;((data as Array<{ company_id?: string; role?: string }> | null) || []).forEach((row) => {
        if (
          row.company_id &&
          (row.role === 'owner' || row.role === 'manager') &&
          !rolesByCompany[row.company_id]
        ) {
          rolesByCompany[row.company_id] = row.role
        }
      })

      companyMembershipRoles.value = rolesByCompany
    } catch (error) {
      console.warn('Unable to fetch company membership context', error)
      companyMembershipRoles.value = {}
    }
  }

  async function ensureAccessContext() {
    if (!isAuthenticated.value || !user.value?.id) {
      return
    }

    await Promise.all([loadUserProfile(user.value.id), loadCompanyMembershipContext()])
  }

  function can(
    resource: PermissionResource,
    action: PermissionAction,
    companyId?: string,
  ): boolean {
    if (!isAuthenticated.value || !user.value) {
      return false
    }

    if (user.value?.is_banned) {
      return false
    }

    if (isAdmin.value) {
      return true
    }

    // Company-scoped resources: check membership role, not just users.role
    const companyScoped: PermissionResource[] = [
      'companies',
      'properties',
      'bookings',
      'guests',
      'screens',
      'announcements',
      'requests',
    ]
    const isCompanyScoped = (companyScoped as string[]).includes(resource)

    if (isCompanyScoped) {
      if (!companyId) {
        // For view without specific company, allow if any membership grants the permission
        const roles = Object.values(companyMembershipRoles.value)
        if (roles.length === 0) return false
        return roles.some((r) => canRole(r, resource, action))
      }
      const membershipRole = companyMembershipRoles.value[companyId]
      if (!membershipRole) return false
      if (!canRole(membershipRole, resource, action)) return false
      if (resource === 'companies' && action === 'edit') {
        return membershipRole === 'owner'
      }
      return true
    }

    if (!canRole(role.value, resource, action)) {
      return false
    }

    if (!companyId) {
      return Object.keys(companyMembershipRoles.value).length > 0
    }

    const membershipRole = companyMembershipRoles.value[companyId]

    if (!membershipRole) {
      return false
    }

    if (resource === 'companies' && action === 'edit') {
      return membershipRole === 'owner'
    }

    return membershipRole === 'owner' || membershipRole === 'manager'
  }

  async function syncUser() {
    if (!token.value) {
      user.value = null
      persistUser(null)
      return
    }

    if (!user.value) {
      const storedUserRaw = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY)
      if (storedUserRaw) {
        try {
          user.value = JSON.parse(storedUserRaw)
        } catch {
          user.value = null
          persistUser(null)
        }
      }
    }

    if (user.value) {
      return
    }

    try {
      const res = await neon.auth.getSession()
      const sessionUser = (res.data as any)?.session?.user ?? null
      if (sessionUser) {
        user.value = sessionUser
        await loadUserProfile(sessionUser.id)
        await loadCompanyMembershipContext()
        persistUser(user.value)
      }
    } catch (e) {
      console.warn('Unable to fetch session info', e)
      user.value = null
      persistUser(null)
    }
  }

  async function login(email: string, password: string, rememberMe = true) {
    const res = await neon.auth.signIn.email({ email, password })

    const sessionToken = res.data?.token ?? null

    if (res.error) {
      console.error('[auth] login failed (backend error)', { email, res })
      throw new Error(
        String((res.error as Record<string, unknown>)?.message || 'Failed to sign in'),
      )
    }

    if (!sessionToken) {
      console.error('[auth] login failed (missing token)', { email, res })
      throw new Error('Failed to sign in')
    }

    token.value = sessionToken
    remember.value = rememberMe

    if (rememberMe) {
      localStorage.setItem(TOKEN_KEY, sessionToken)
      sessionStorage.removeItem(SESSION_TOKEN_KEY)
      localStorage.setItem(REMEMBER_KEY, 'true')
    } else {
      sessionStorage.setItem(SESSION_TOKEN_KEY, sessionToken)
      localStorage.removeItem(TOKEN_KEY)
      localStorage.setItem(REMEMBER_KEY, 'false')
    }

    if (res.data.user) {
      user.value = res.data.user
      await loadUserProfile(res.data.user.id)
      await loadCompanyMembershipContext()
      persistUser(user.value)
      useThemeStore().syncFromDb(res.data.user.id)
    } else {
      user.value = null
      persistUser(null)
      await syncUser()
    }

    if (!isApproved.value && role.value && role.value !== 'user') {
      await router.push('/pending-approval')
      throw new Error('Your account is pending approval.')
    }

    if (user.value?.id) {
      updateLastLoginInDb(user.value.id)
      import('@/services/eventLog').then(({ trackSession }) => {
        trackSession(user.value?.id || '', 'login')
      })
    }

    await router.push('/')
  }

  async function signUp(
    email: string,
    password: string,
    name: string,
    inviteToken?: string | null,
    referralCode?: string | null,
  ) {
    const res = await neon.auth.signUp.email({ email, password, name })

    if (res.error) {
      console.error('[auth] signUp failed (backend error)', { email, res })
      throw new Error(res.error?.message ?? 'Failed to sign up')
    }

    const userId = res.data?.user?.id

    if (userId) {
      const { generateReferralCode, trackReferral } = await import('@/services/referrals')
      const userReferralCode = generateReferralCode(name)

      try {
        await neon.from('users').upsert(
          {
            id: userId,
            referral_code: userReferralCode,
          },
          { onConflict: 'id' },
        )
      } catch {
        // non-critical
      }

      if (referralCode) {
        try {
          await trackReferral(referralCode, email, userId)
        } catch {
          // non-critical
        }
      }
    }

    if (inviteToken && userId) {
      try {
        const { parseInviteToken } = await import('@/lib/invites')
        const invite = parseInviteToken(inviteToken)
        if (invite) {
          await neon
            .from('users')
            .update({
              is_approved: true,
              role: invite.role,
              company_id: invite.company_id,
            })
            .eq('id', userId)
        }
      } catch (e) {
        console.error('[auth] invite processing failed', e)
      }
    } else if (userId) {
      // Customer self-registration: auto-approved, base user role
      await neon
        .from('users')
        .update({
          is_approved: true,
        })
        .eq('id', userId)
    }

    lastRegisteredEmail.value = email
    await router.push('/verify-email')
  }

  async function restoreSession() {
    initializing.value = true
    try {
      const tokenFromLocal = localStorage.getItem(TOKEN_KEY)
      const tokenFromSession = sessionStorage.getItem(SESSION_TOKEN_KEY)

      if (!tokenFromLocal && !tokenFromSession) {
        token.value = null
        user.value = null
        remember.value = false
        companyMembershipRoles.value = {}
        return false
      }

      token.value = tokenFromLocal || tokenFromSession
      remember.value = !!tokenFromLocal

      // Strict remember=false: logout on reload (not just new tab)
      if (!remember.value && token.value) {
        const isReload = (() => {
          try {
            const nav = (performance as any).getEntriesByType?.('navigation')?.[0] as
              | PerformanceNavigationTiming
              | undefined
            if (nav?.type === 'reload') return true
            const legacy = (performance as any).navigation
            if (legacy?.type === 1) return true
            return false
          } catch {
            return false
          }
        })()
        if (isReload) {
          token.value = null
          user.value = null
          remember.value = false
          companyMembershipRoles.value = {}
          persistUser(null)
          sessionStorage.removeItem(SESSION_TOKEN_KEY)
          sessionStorage.removeItem(USER_KEY)
          localStorage.removeItem(TOKEN_KEY)
          return false
        }
      }

      if (!user.value) {
        const storedUserRaw = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY)
        if (storedUserRaw) {
          try {
            user.value = JSON.parse(storedUserRaw)
          } catch {
            user.value = null
            persistUser(null)
          }
        }
      }

      if (!user.value) {
        await syncUser()
      } else {
        await ensureAccessContext()
      }

      if (user.value?.id) {
        useThemeStore().syncFromDb(user.value.id)
      }

      return isAuthenticated.value
    } finally {
      initializing.value = false
    }
  }

  async function getSession() {
    const res = await neon.auth.getSession()
    if (res.error) {
      throw new Error(res.error.message)
    }
    return res.data?.session ?? null
  }

  async function verifyEmail(code: string): Promise<void> {
    const res = await neon.auth.verifyEmail({ query: { token: code } })

    if (res.error) {
      console.error('[auth] verifyEmail failed', res)
      throw new Error(res.error.message ?? 'Verification failed')
    }

    if (res.data?.status) {
      await syncUser()
    }
  }

  async function resendVerification(email: string): Promise<void> {
    const res = await neon.auth.sendVerificationEmail({ email })
    if (res.error) {
      throw new Error(res.error.message ?? 'Failed to resend verification code')
    }
  }

  async function logout() {
    const uid = user.value?.id

    try {
      await neon.auth.signOut()
    } catch (ignore) {
      // ignore sign out errors, we still clear local state
    }

    if (uid) {
      import('@/services/eventLog').then(({ trackSession }) => {
        trackSession(uid, 'logout')
      })
    }

    token.value = null
    user.value = null
    remember.value = false
    companyMembershipRoles.value = {}
    persistUser(null)
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REMEMBER_KEY)
    sessionStorage.removeItem(SESSION_TOKEN_KEY)
    sessionStorage.removeItem(USER_KEY)
    message.success('Logged out successfully.')
    router.push('/login')
  }

  // If auth store initialization sees a token but no user, attempt to restore
  // user profile from stored data or backend.
  if (token.value && !user.value) {
    syncUser().catch(() => {})
  }

  function updateUser(partial: Record<string, any>) {
    if (user.value) {
      user.value = { ...user.value, ...partial }
      persistUser(user.value)
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('auth:expired', () => {
      token.value = null
      user.value = null
      remember.value = false
      companyMembershipRoles.value = {}
      localStorage.removeItem(TOKEN_KEY)
      persistUser(null)
      sessionStorage.removeItem(SESSION_TOKEN_KEY)
      sessionStorage.removeItem(USER_KEY)
    })

    // Cross-tab sync: if token/user changes in another tab, update this tab
    window.addEventListener('storage', (event) => {
      if (
        event.key === TOKEN_KEY ||
        event.key === SESSION_TOKEN_KEY ||
        event.key === USER_KEY ||
        event.key === REMEMBER_KEY
      ) {
        const newToken =
          localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(SESSION_TOKEN_KEY)
        const newUserRaw = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY)
        const newUser = newUserRaw ? JSON.parse(newUserRaw) : null

        // If token was removed in another tab (logout), clear this tab
        if (!newToken && token.value) {
          token.value = null
          user.value = null
          remember.value = false
          companyMembershipRoles.value = {}
          message.info('Session ended in another tab.')
          router.push('/login')
        } else if (newToken && newToken !== token.value) {
          // Token changed (login in another tab)
          token.value = newToken
          user.value = newUser
          remember.value = localStorage.getItem(REMEMBER_KEY) === 'true'
          if (user.value?.id) {
            ensureAccessContext().catch(() => {})
          }
        } else if (JSON.stringify(newUser) !== JSON.stringify(user.value)) {
          user.value = newUser
          if (user.value?.id) {
            ensureAccessContext().catch(() => {})
          } else {
            companyMembershipRoles.value = {}
          }
        }
      }
    })
  }

  return {
    token,
    user,
    remember,
    isAuthenticated,
    role,
    isAdmin,
    isApproved,
    isEmailVerified,
    lastRegisteredEmail,
    companyMembershipRoles,
    initializing,
    can,
    login,
    signUp,
    verifyEmail,
    resendVerification,
    logout,
    syncUser,
    getSession,
    restoreSession,
    ensureAccessContext,
    updateUser,
  }
})
